import {
  DataFieldWithDataType,
  getKeyFromDataFieldWithDataType,
} from '../../../../common-types';
import { HierarchicalFields } from '../../../common/api/api-interfaces';
import { isHiddenField } from '../../../common/company-data-view-settings';
// GetNonNullFieldsForDataTypeService is typed against the legacy DataTypes enum, while
// DataFieldWithDataType uses the v2 one. Same string values, separate declarations.
import { DataTypes as LegacyDataTypes } from '../../../common/constants/constants';
import { GetNonNullFieldsForDataTypeService } from '../../../common/services/GetNonNullFieldsForDataTypeService';
import { ExecutorRoleViewAuthorizationService } from '../../../common/v2/api/authorization/service';
import { Domains } from '../../../common/v2/constants';
import {
  AllUsedFields,
  DataFields,
  DataTypes,
  EmployeeDataFields,
} from '../../../common/v2/types';
import { nonUserVisibleFields } from '../../../common/v2/utils';
import { DrillDownColumn, EnrichmentCacheEntry } from './types';

const employeeField = (dataField: DataFields): DataFieldWithDataType => ({
  dataType: DataTypes.EMPLOYEE,
  dataField,
});

/**
 * The row key. Always queried, always rendered first, and never offered in the column
 * picker — the table cannot identify a row without it.
 */
export const PINNED_FIELD = employeeField(EmployeeDataFields.EMPLOYEE_ID);

/**
 * Shown when a role has not customised its columns. Mirrors the columns the drill-down
 * rendered before it became configurable, minus {@link PINNED_FIELD}.
 */
export const DEFAULT_DRILLDOWN_FIELDS: DataFieldWithDataType[] = [
  employeeField(EmployeeDataFields.FULL_NAME),
  employeeField(EmployeeDataFields.JOB_TITLE),
  employeeField(EmployeeDataFields.GENDER),
  employeeField(EmployeeDataFields.ORGANIZATION_LEVEL_1),
  employeeField(EmployeeDataFields.LOCATION_LEVEL_1),
  employeeField(EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_1),
];

export interface ColumnLayout {
  /**
   * Flex basis and hard minimum, in px. Columns grow to share any spare width, but never
   * shrink below this — past ~7 columns the table overflows and scrolls sideways instead of
   * squeezing every header down to an unreadable "Start …".
   */
  minWidth: number;
}

const DEFAULT_COLUMN_MIN_WIDTH = 165;

/** Widths tuned to the shape of the value: codes and enums are narrow, names and orgs wide. */
const COLUMN_MIN_WIDTHS: Partial<Record<EmployeeDataFields, number>> = {
  [EmployeeDataFields.EMPLOYEE_ID]: 150,
  [EmployeeDataFields.GENDER]: 110,
  [EmployeeDataFields.FULL_NAME]: 190,
  [EmployeeDataFields.LOCAL_FULL_NAME]: 190,
  [EmployeeDataFields.ORGANIZATION_LEVEL_1]: 190,
};

/** Header and body cells must share this, or the two grids drift out of alignment. */
export const getColumnLayout = (
  field: DataFieldWithDataType
): ColumnLayout => ({
  minWidth:
    // Candidates are always employee fields (see getDrillDownCandidateFields);
    // DataFieldWithDataType just can't express that narrower guarantee.
    COLUMN_MIN_WIDTHS[field.dataField as EmployeeDataFields] ??
    DEFAULT_COLUMN_MIN_WIDTH,
});

/** Width of the Value column (sort + filter icon); not an employee field. */
export const VALUE_COLUMN_MIN_WIDTH = 190;

/** Width of the leading row-number ("No.") column. Fixed (no flex-grow). */
export const ROW_NUMBER_COLUMN_MIN_WIDTH = 56;

export const FULL_NAME_KEY = getKeyFromDataFieldWithDataType(
  employeeField(EmployeeDataFields.FULL_NAME)
);
export const JOB_TITLE_KEY = getKeyFromDataFieldWithDataType(
  employeeField(EmployeeDataFields.JOB_TITLE)
);

/**
 * Values for the fallback enrichment path, which can only resolve the name and job title.
 * Every other selected column stays blank there — hence the picker is disabled when the
 * advanced-query path is unavailable.
 */
export const employeeToEnrichmentEntry = (employee: {
  fullName?: string;
  jobTitle?: string;
}): EnrichmentCacheEntry => ({
  [FULL_NAME_KEY]: employee.fullName,
  [JOB_TITLE_KEY]: employee.jobTitle,
});

/**
 * Every employee field this domain could show: populated with data, user-visible, and not
 * hidden for the domain. Deliberately NOT filtered by permissions — the picker greys out
 * fields the role cannot see rather than hiding their existence, so the caller applies
 * {@link ExecutorRoleViewAuthorizationService.canSeeField} itself.
 */
export const getDrillDownCandidateFields = (
  domain: Domains,
  allUsedFields: AllUsedFields
): DataFieldWithDataType[] => {
  const excludedKeys = new Set(
    [...nonUserVisibleFields, ...HierarchicalFields].map(
      getKeyFromDataFieldWithDataType
    )
  );
  return GetNonNullFieldsForDataTypeService(
    LegacyDataTypes.EMPLOYEE,
    allUsedFields
  )
    .filter((f) => !excludedKeys.has(getKeyFromDataFieldWithDataType(f)))
    .filter((f) => !isHiddenField(f, domain))
    .filter((f) => f.dataField !== PINNED_FIELD.dataField);
};

/**
 * Pairs each saved field with whether the role may still read it.
 *
 * A field the role has lost access to keeps its column (rendered as CONFIDENTIAL) but is
 * marked inaccessible so callers can keep it out of the query. A field that is no longer a
 * candidate at all — newly hidden for the domain — is dropped outright, since there is
 * nothing to withhold.
 *
 * `candidateFields` is built from `allUsedFields`, which is fetched through a query that is
 * itself re-run per simulated/impersonated role (see GraphQlDomainDependenciesService) — so
 * "not a candidate" can mean "this role's backend query omitted the field" rather than
 * "nobody has this data." That distinction cannot be trusted for a field the role can't see
 * anyway (its value is masked regardless), so such fields are kept — and marked
 * inaccessible — even when they fell out of `candidateFields`. Only `isHiddenField`, a
 * static per-domain config unrelated to role, is treated as a real "this doesn't exist"
 * signal.
 */
export const resolveDrillDownColumns = (
  fields: DataFieldWithDataType[],
  candidateFields: DataFieldWithDataType[],
  domain: Domains,
  authorizationService: ExecutorRoleViewAuthorizationService
): DrillDownColumn[] => {
  const candidateKeys = new Set(
    candidateFields.map(getKeyFromDataFieldWithDataType)
  );
  return fields
    .filter((f) => !isHiddenField(f, domain))
    .filter(
      (f) =>
        candidateKeys.has(getKeyFromDataFieldWithDataType(f)) ||
        !authorizationService.canSeeField(f)
    )
    .map((field) => ({
      field,
      accessible: authorizationService.canSeeField(field),
    }));
};
